import type { CatalogForm, Person, Screeve } from '@nelnela/contracts';

/**
 * The verb forms panel's decisions (M10-iii), kept out of the screen so they can be tested.
 *
 * α's headline call: **not the grid — one person's column at a time.** Six Georgian person labels
 * do not fit a SegmentedControl at 360 px, and a full screeve × person table is a spreadsheet a
 * learner has to read rather than a paradigm they can. So the pickers pin at the top, the screeves
 * scroll under them, and the panel answers one question at a time: "how do I say this, from me,
 * to you".
 *
 * WHAT SHIPS TODAY IS THE EMPTY STATE. `catalog_form` is empty behind the UniMorph gate (M10-i §
 * What is DISABLED on purpose): forms ship only at ≥ 95 % exact against UniMorph `kat`'s 118
 * native-verified tables, and until then `catalogForms` returns null for every entry. That is a
 * shipped state, not a failure — the ROADMAP gate's else-arm, "else no forms panel" — so this
 * module and its tests run against fixtures, and they are the only place the panel is known to
 * work before the gate opens.
 */

/** α's WHO row, in `PERSONS` order minus the sentinel. */
export const SUBJECT_LABELS: Partial<Record<Person, string>> = {
  '1sg': 'მე',
  '2sg': 'შენ',
  '3sg': 'ის',
  '1pl': 'ჩვენ',
  '2pl': 'თქვენ',
  '3pl': 'ისინი',
};

/**
 * α's → TO WHOM row. The object pronouns are not the subject pronouns: Georgian's third-person
 * object is მას / მათ, not ის / ისინი, and showing the subject set here would teach a wrong form
 * on the screen whose whole job is teaching forms.
 */
export const OBJECT_LABELS: Partial<Record<Person, string>> = {
  '-': '—',
  '1sg': 'მე',
  '2sg': 'შენ',
  '3sg': 'მას',
  '1pl': 'ჩვენ',
  '2pl': 'თქვენ',
  '3pl': 'მათ',
};

/** English for the readout line under the pickers. */
const SUBJECT_EN: Partial<Record<Person, string>> = {
  '1sg': 'I',
  '2sg': 'you',
  '3sg': 'he/she',
  '1pl': 'we',
  '2pl': 'you (pl)',
  '3pl': 'they',
};
const OBJECT_EN: Partial<Record<Person, string>> = {
  '1sg': 'me',
  '2sg': 'you',
  '3sg': 'him/her',
  '1pl': 'us',
  '2pl': 'you (pl)',
  '3pl': 'them',
};

export const SCREEVE_LABELS: Record<Screeve, string> = {
  present: 'PRESENT',
  imperfect: 'IMPERFECT',
  future: 'FUTURE',
  aorist: 'AORIST',
  optative: 'OPTATIVE',
  perfect: 'PERFECT',
  masdar: 'MASDAR',
  imperative: 'IMPERATIVE',
};

/**
 * Does this verb take an object?
 *
 * α: "→ TO WHOM appears only for polypersonal verbs". Asked of the DATA rather than of the part of
 * speech, because the paradigm is the only thing that knows: an intransitive verb carries `'-'` in
 * every object cell, so a row of pickers over it would offer six choices that all resolve to the
 * same column. The row is then absent, not disabled — α: "ნერვიულობა takes none, so the row is
 * absent".
 */
export const isPolypersonal = (forms: readonly CatalogForm[]): boolean =>
  forms.some((f) => f.object !== '-');

/** Which subjects this paradigm actually has, in `PERSONS` order (the server already sorted). */
export function subjectsPresent(forms: readonly CatalogForm[]): Person[] {
  const seen = new Set<Person>();
  for (const f of forms) if (f.subject !== '-') seen.add(f.subject);
  return (Object.keys(SUBJECT_LABELS) as Person[]).filter((p) => seen.has(p));
}

/** Which objects this paradigm has, including the `'-'` sentinel when some cell has no object. */
export function objectsPresent(forms: readonly CatalogForm[]): Person[] {
  const seen = new Set<Person>();
  for (const f of forms) seen.add(f.object);
  return (Object.keys(OBJECT_LABELS) as Person[]).filter((p) => seen.has(p));
}

export interface Cell {
  screeve: Screeve;
  label: string;
  form: string;
  attested: boolean;
}

/**
 * The screeve column for one person pair — the panel's body.
 *
 * `masdar` and `imperative` are excluded and handled by `asideCells`; see there for why.
 */
export function personColumn(
  forms: readonly CatalogForm[],
  subject: Person,
  object: Person,
): Cell[] {
  return forms
    .filter(
      (f) =>
        f.screeve !== 'masdar' &&
        f.screeve !== 'imperative' &&
        f.subject === subject &&
        f.object === object,
    )
    .map(toCell);
}

/**
 * The block that stands outside the person picker — α's "WITHOUT A PERSON".
 *
 * The masdar belongs here plainly: `generate_forms.py` emits it as one cell with subject and
 * object both `'-'`, the citation form itself.
 *
 * THE IMPERATIVE IS HERE TOO, and that is a deliberate reading of α rather than of the data. The
 * generator gives it 2sg and 2pl only ("For imperative: give 2sg and 2pl only"), so strictly it
 * has a subject and would sit inside the person column. But its person is fixed by the mood, not
 * chosen — you cannot command yourself — so putting it in the column would hide it from every
 * learner who happened to have მე picked, and α singles it out as "the form learners actually
 * text". A picker that changes it would be offering a choice that is not real.
 */
export function asideCells(forms: readonly CatalogForm[]): Cell[] {
  return forms.filter((f) => f.screeve === 'masdar' || f.screeve === 'imperative').map(toCell);
}

function toCell(f: CatalogForm): Cell {
  const label =
    f.screeve === 'imperative' && f.subject !== '-'
      ? `${SCREEVE_LABELS.imperative} · ${SUBJECT_LABELS[f.subject] ?? f.subject}`
      : SCREEVE_LABELS[f.screeve];
  return { screeve: f.screeve, label, form: f.form, attested: f.attested };
}

/**
 * The readout under the pickers: `მე → შენ · "I … to you"`.
 *
 * α asks for the pair "in plain words", because two pronoun chips selected in two rows is a state
 * a learner has to hold in their head, and the sentence is the thing they were actually asking.
 */
export function readout(subject: Person, object: Person): string {
  const ka = SUBJECT_LABELS[subject] ?? subject;
  const en = SUBJECT_EN[subject] ?? subject;
  if (object === '-') return `${ka} · “${en} …”`;
  const kaO = OBJECT_LABELS[object] ?? object;
  const enO = OBJECT_EN[object] ?? object;
  return `${ka} → ${kaO} · “${en} … to ${enO}”`;
}

/**
 * The first pair to show when the panel opens.
 *
 * 1sg where the paradigm has it, because "how do I say it" is the question a learner brings; the
 * first subject present otherwise, so a defective paradigm opens on something rather than on an
 * empty column.
 */
export function initialPair(forms: readonly CatalogForm[]): { subject: Person; object: Person } {
  const subjects = subjectsPresent(forms);
  const subject = subjects.includes('1sg') ? '1sg' : (subjects[0] ?? '1sg');
  if (!isPolypersonal(forms)) return { subject, object: '-' };
  const objects = objectsPresent(forms).filter((o) => o !== '-');
  return { subject, object: objects.includes('2sg') ? '2sg' : (objects[0] ?? '-') };
}
